/**
 * EditorFormatButton — sl-93dd.5
 *
 * Icon-only `<IconButton>` that runs `dedentCode` against the current
 * editor value and emits the dedented result through `onChange`.
 *
 * The paste handler (sl-93dd.2) and save normalizer (sl-93dd.3) already
 * dedent automatically in most flows, so this button is a manual escape
 * hatch for code that bypassed both paths (e.g. code drag-dropped into
 * the editor, code from a historical persisted config). It disables when
 * there is nothing to fix so it never appears as a meaningless no-op
 * click target.
 */

import { IconButton } from '@affine/component/ui/button';
import { AlignLeftIcon } from '@blocksuite/icons/rc';
import { useCallback, useMemo } from 'react';

import type { CodeEditorLanguage } from './code-editor';
import type { FixIndentLanguage } from './internal/fix-indent';
import { fixIndent } from './internal/fix-indent';

interface EditorFormatButtonProps {
  value: string;
  onChange: (value: string) => void;
  language: CodeEditorLanguage;
  readOnly?: boolean;
}

export function EditorFormatButton({
  value,
  onChange,
  language,
  readOnly,
}: EditorFormatButtonProps) {
  const fixed = useMemo(
    () => fixIndent(value, language as FixIndentLanguage),
    [value, language]
  );
  const disabled = !!readOnly || fixed === value;

  const handleClick = useCallback(() => {
    if (disabled) return;
    onChange(fixed);
  }, [disabled, fixed, onChange]);

  return (
    <IconButton
      size={20}
      icon={<AlignLeftIcon />}
      onClick={handleClick}
      disabled={disabled}
      tooltip="Einrückung korrigieren"
    />
  );
}
